import { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import {
  FinayaAccessApi,
  AccessData,
  InventoryItem,
  ClientItem,
  InitiativeItem,
  AccessRequestPayload,
  AccessRequestResult,
  AccessRequest,
  RequestComment,
} from './FinayaAccessApi';

export class FinayaAccessClient implements FinayaAccessApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: {
    discoveryApi: DiscoveryApi;
    fetchApi: FetchApi;
  }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  async getMyAccess(): Promise<AccessData> {
    const baseUrl = await this.discoveryApi.getBaseUrl('finaya-access');
    const response = await this.fetchApi.fetch(`${baseUrl}/me`);

    if (!response.ok) {
      throw new Error(
        `Erro ao buscar acessos: ${response.status} ${response.statusText}`,
      );
    }

    return response.json();
  }

  async getInventory(): Promise<InventoryItem[]> {
    const baseUrl = await this.discoveryApi.getBaseUrl('finaya-access');
    const response = await this.fetchApi.fetch(`${baseUrl}/inventory`);

    if (!response.ok) {
      throw new Error(
        `Erro ao buscar ferramentas: ${response.status} ${response.statusText}`,
      );
    }

    return response.json();
  }

  async getClients(): Promise<ClientItem[]> {
    const baseUrl = await this.discoveryApi.getBaseUrl('finaya-access');
    const response = await this.fetchApi.fetch(`${baseUrl}/clients`);

    if (!response.ok) {
      throw new Error(
        `Erro ao buscar clientes: ${response.status} ${response.statusText}`,
      );
    }

    return response.json();
  }

  async getInitiatives(clientePageId: string): Promise<InitiativeItem[]> {
    const baseUrl = await this.discoveryApi.getBaseUrl('finaya-access');
    const response = await this.fetchApi.fetch(
      `${baseUrl}/initiatives?clientePageId=${encodeURIComponent(clientePageId)}`,
    );

    if (!response.ok) {
      throw new Error(
        `Erro ao buscar iniciativas: ${response.status} ${response.statusText}`,
      );
    }

    return response.json();
  }

  async createAccessRequest(
    payload: AccessRequestPayload,
  ): Promise<AccessRequestResult> {
    const baseUrl = await this.discoveryApi.getBaseUrl('finaya-access');
    const response = await this.fetchApi.fetch(`${baseUrl}/requests`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(
        (body as any).error ||
          `Erro ao criar solicitacao: ${response.status} ${response.statusText}`,
      );
    }

    return response.json();
  }

  async getMyRequests(): Promise<AccessRequest[]> {
    const baseUrl = await this.discoveryApi.getBaseUrl('finaya-access');
    const response = await this.fetchApi.fetch(`${baseUrl}/requests/mine`);

    if (!response.ok) {
      throw new Error(
        `Erro ao buscar solicitacoes: ${response.status} ${response.statusText}`,
      );
    }

    return response.json();
  }

  async getRequestComments(pageId: string): Promise<RequestComment[]> {
    const baseUrl = await this.discoveryApi.getBaseUrl('finaya-access');
    const response = await this.fetchApi.fetch(
      `${baseUrl}/requests/${encodeURIComponent(pageId)}/comments`,
    );

    if (!response.ok) {
      throw new Error(
        `Erro ao buscar comentarios: ${response.status} ${response.statusText}`,
      );
    }

    return response.json();
  }

  async addRequestComment(
    pageId: string,
    text: string,
  ): Promise<RequestComment> {
    const baseUrl = await this.discoveryApi.getBaseUrl('finaya-access');
    const response = await this.fetchApi.fetch(
      `${baseUrl}/requests/${encodeURIComponent(pageId)}/comments`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text }),
      },
    );

    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(
        (body as any).error ||
          `Erro ao criar comentario: ${response.status} ${response.statusText}`,
      );
    }

    return response.json();
  }
}
